/* global TradeInValuations */
import React, { Component } from "react"
import PropTypes from "prop-types"
import MDSpinner from "react-md-spinner"
import deepmerge from "deepmerge"

import BrandAndModelSelector from "./BrandAndModelSelector"
import SingleChoice from "./SingleChoice"
import Step from "./Step"

import styles from "./CarAssemblage.module.sass"
import stepsConfig, { ROOT_OBJECT_NAME } from "./stepsConfig"
import { validateSelected } from "./helpers/stepsHelpers"
import BrandSelector from "./BrandSelector"
import ModelSelector from "./ModelSelector"

// For some reason the linter displays errors even if i set the propTypes
const NonSingleStep = React.forwardRef((props, ref) => (
  <Step
    theme={props.theme}
    ref={ref}
    active={props.active}
    {...props.step}
    index={props.index}
    labels={props.labels}
    navigatedTo={props.navigatedTo}
    headline={props.headline}
    hideStepsArrow={props.hideStepsArrow}
  >
    {props.children}
  </Step>
))

class CarAssemblage extends Component {
  static propTypes = {
    labels: PropTypes.object.isRequired,
    finishedCallback: PropTypes.func,
    brandAndModelOptions: PropTypes.object.isRequired,
    conditionOptions: PropTypes.array,
    conditionAttributes: PropTypes.array,
    intendedCarChangePeriods: PropTypes.array,
    departmentOptions: PropTypes.array,
    departmentParamName: PropTypes.string,
    trackFunction: PropTypes.func,
    initialAssembledCar: PropTypes.object,
    initialBrandModelText: PropTypes.string,
    scrollStepsIntoView: PropTypes.bool,
    autoFocusBrandModel: PropTypes.bool,
    theme: PropTypes.object.isRequired,
    kmPlaceholder: PropTypes.string,
    locale: PropTypes.string,
    postcodesForTypeaheadUrl: PropTypes.string,
    onCapturePostCode: PropTypes.func,
    onCaptureBuyerEmailAddress: PropTypes.func,
    emailAddressTranslations: PropTypes.object,
    enableReengagementFlow: PropTypes.bool,
    fromTestDriveForm: PropTypes.bool,
    hideFutureSteps: PropTypes.bool,
    onProgress: PropTypes.func,
    hideStepsArrow: PropTypes.bool,
    onProceedButtonRender: PropTypes.func,
    buttonComponent: PropTypes.any,
    isSellYourCar: PropTypes.bool,
    stepsConfigBuilder: PropTypes.func,
    ctaAlwaysEnabled: PropTypes.bool,
    onCurrentStepChange: PropTypes.func,
    hideFormButton: PropTypes.bool,
    onFormCompleted: PropTypes.func,
    user: PropTypes.object,
  }

  state = {
    steps:
      this.props.isSellYourCar && this.props.stepsConfigBuilder
        ? this.props.stepsConfigBuilder(this.props)
        : stepsConfig(this.props),
    currentStep: 0,
    loading: false,
    finalized: false,
  }

  constructor(props) {
    super(props)
    this.stepsRef = []
    this.nonSingleStepsNum = props.isSellYourCar ? 2 : 1
    this.state.steps.forEach((step, i) => {
      this.stepsRef[i] = React.createRef()
    })
  }

  areStepsComplete() {
    if (
      this.state.currentStep === this.state.steps.length - 1 &&
      this.props.enableReengagementFlow
    ) {
      this.props.onLastStep && this.props.onLastStep()
      return true
    } else {
      return this.state.steps.every(({ completed }) => completed)
    }
  }

  componentDidMount() {
    // Update steps based on initial car info
    let initialAssembledCarDetected = false
    if (this.props.initialAssembledCar) {
      this.state.steps.forEach((step, stepIndex) => {
        if (!step.selectedFromCar) return
        const selected = step.selectedFromCar(
          this.props.initialAssembledCar,
          step.options
        )
        if (selected && Object.keys(selected).length) {
          this.changeStepSelected(stepIndex, selected, {
            completionSource: "initialAssembledCar",
          })
          initialAssembledCarDetected = true
        }
      })
      if (this.props.trackFunction && initialAssembledCarDetected) {
        this.props.trackFunction({
          entity: "car_form",
          action: "displayed",
          details: "prefilled with LPN data",
          flow: "trade_in",
          type: "micro_proceeding",
        })
      }
    }
    // Track initial loading event
    if (this.props.trackFunction) {
      this.props.trackFunction({
        entity: "car_form",
        action: "displayed",
        details: [
          this.props.hideFutureSteps ? "with hidden next steps" : "",
          this.props.initialBrandModelText
            ? "with initial brand model text"
            : "",
        ]
          .filter(Boolean)
          .join(", "),
        flow: "trade_in",
        type: "micro_proceeding",
      })
    }
    if (typeof TradeInValuations !== "undefined")
      TradeInValuations.sendMessage({
        action: "TradeInCalculator.carDetailsStarted",
      })

    if (this.props.onProceedButtonRender) {
      this.props.onProceedButtonRender(this.renderProceedButton())
    }
  }

  componentWillUnmount() {
    const container = document.querySelector("header")
    if (container) container.style.marginTop = "0px"
  }

  componentDidUpdate(_prevProps, prevState) {
    if (
      prevState.currentStep !== this.state.currentStep &&
      this.state.currentStep === -1 &&
      this.props.onFormCompleted
    ) {
      this.props.onFormCompleted(this.generateFinishedPayload())
    }
    if (
      prevState.currentStep !== this.state.currentStep &&
      this.state.currentStep !== -1
    ) {
      if (this.props.onCurrentStepChange) {
        this.props.onCurrentStepChange(this.state.currentStep, this.state.steps)
      }
      this.scrollToStep(this.state.currentStep)
      this.props.trackFunction({
        entity: "car_form",
        element: `select option: ${this.state.steps[this.state.currentStep].stepName
          }`,
        action: "displayed",
        flow: "trade_in",
        type: "micro_proceeding",
      })
    }
    if (prevState.steps !== this.state.steps) {
      this.state.steps.forEach((step, i) => {
        const prevStep = prevState.steps[i]
        if (prevStep === step) return

        const selectedChanged = prevStep.selected !== step.selected
        const optionsChanged = prevStep.options !== step.options

        // When a step has a delayed selectedFromCar and its options have changed, then set the selected
        if (
          optionsChanged &&
          this.props.initialAssembledCar &&
          step.selectedFromCarDelayed
        ) {
          this.changeStepSelected(
            i,
            step.selectedFromCarDelayed(
              this.props.initialAssembledCar,
              step.options
            ),
            {
              completionSource: "initialAssembledCar",
            }
          )
          if (step.apiCall) this.fetchCarAssemblageStep(i, step)
          return
        }

        // Do not trigger anything if nothing is selected on this step, or if there are no options
        if (step.selected === null || step.options === null) return

        // Trigger onProgress when nr of completed steps change
        if (step.completed !== prevStep.completed && this.props.onProgress) {
          this.props.onProgress(
            this.state.steps.filter(({ completed }) => completed).length,
            this.state.steps.length
          )
        }

        // Do not trigger anything if nothing relevant has changed
        if (!selectedChanged && !optionsChanged) return

        const validation = validateSelected(step, this.state.steps)
        if (validation) {
          // Invalidate selected by setting to null
          this.setState((prevState) => {
            const steps = [...prevState.steps]
            steps[i] = {
              ...steps[i],
              selected: null,
              completed: false,
              valid: false,
              validation,
            }
            return {
              steps,
              currentStep: this.currentStepBasedOnCompleted(steps),
            }
          })
        } else {
          this.changeStepSelected(i)
          if (step.apiCall) this.fetchCarAssemblageStep(i, step)
        }
      })
    }
  }

  // Optional given a (valid) selected, it will also update it
  changeStepSelected(
    stepIndex,
    selected,
    { forceComplete = false, completionSource, callback } = {}
  ) {
    this.setState((prevState) => {
      const steps = [...prevState.steps]
      steps[stepIndex] = { ...steps[stepIndex] }
      if (forceComplete) {
        this.props.trackFunction({
          entity: "car_form",
          action: "changed",
          element: `select option: ${this.state.steps[stepIndex].stepName}`,
          flow: "trade_in",
          type: "micro_proceeding",
        })
        // Only complete the step in impasse if the selected has changed
        if (
          !steps[stepIndex].impasse ||
          (selected &&
            steps[stepIndex] &&
            steps[stepIndex].selected !== selected)
        ) {
          steps[stepIndex].completed = true
        }
      } else if (
        steps[stepIndex].options &&
        steps[stepIndex].completeAutomatically
      ) {
        // Only complete automatically when both options and selected are present
        steps[stepIndex].completed = true
        this.props.trackFunction({
          entity: "car_form",
          action: "changed",
          element: `select option: ${this.state.steps[stepIndex].stepName}`,
          details: ["prefilled", steps[stepIndex].completionSource ?? ""]
            .filter(Boolean)
            .join(", "),
          flow: "trade_in",
          type: "micro_proceeding",
        })
      }

      if (completionSource) steps[stepIndex].completionSource = completionSource

      if (selected) {
        steps[stepIndex].selected =
          steps[stepIndex].stepName === "equipment" && steps[stepIndex].selected
            ? {
              ...steps[stepIndex].selected,
              ...selected,
            }
            : selected
      }
      // Validate steps which are forced complete (changes coming from user)
      const validation = forceComplete
        ? validateSelected(steps[stepIndex], steps)
        : null
      steps[stepIndex].valid = !validation
      if (validation) steps[stepIndex].completed = false
      steps[stepIndex].validation = validation
      return {
        steps,
        currentStep: this.currentStepBasedOnCompleted(steps),
      }
    }, callback)
  }

  fetchCarAssemblageStep(stepIndex, step) {
    this.setState({ loading: true })
    fetch(`/da/car_assemblage/${step.apiName || step.stepName}.json`, {
      method: "POST",
      body: JSON.stringify({
        ...this.generateFinishedPayload(),
        context: "trade_in",
      }),
      headers: new Headers({ "Content-Type": "application/json" }),
    })
      .then((res) => res.json()) // TODO: Handle failure when res status is not 200
      .then((data) => this.updateStepOptions(stepIndex, data))
  }

  finished = () => {
    if (!this.props.finishedCallback) return
    this.setState({ finalized: true })

    this.props.finishedCallback(this.generateFinishedPayload())
  }

  /**
   * Generates the payload object that is going to be sent to the server
   * including all the details entered in the interface
   *
   * @returns {object}
   */
  generateFinishedPayload = () => {
    let payload = {}
    this.state.steps.forEach((step) => {
      if (step.selectedObjectName === ROOT_OBJECT_NAME) {
        // When a step has values for different top-level objects,
        // we deep merge them with the payload
        payload = deepmerge(payload, step.selected || {})
      } else if (typeof step.selected === "string") {
        payload[step.selectedObjectName] = step.selected
      } else {
        payload[step.selectedObjectName] = {
          ...payload[step.selectedObjectName],
          ...step.selected,
        }
      }
    })
    return payload
  }

  // Returns the current step based on what step is completed (selected)
  // The active step will be the first one that is incomplete
  // When all steps are complete, the current step is -1
  currentStepBasedOnCompleted = (steps) =>
    steps.findIndex((step) => !step.completed)

  // stepIndex: the step that was updated
  updateStepOptions = (stepIndex, data) => {
    this.setState((prevState) => {
      let steps = [...prevState.steps]

      // `possible_choices` is the source of truth for the step names
      const stepNames = Object.keys(data["possible_choices"])

      stepNames.forEach((stepName) => {
        const index = this.findStepIndexByApiIdOrName(steps, stepName)
        const options = this.mapApiResponseToStepChoices(steps[index], data)
        const allChoices =
          data["all_choices"]?.[steps[index].stepName].map(
            this.getValueToLabelMapper(steps[index])
          ) || []
        const manualInputs =
          data["manual_inputs"]?.[steps[index].stepName] || []
        const initiallyPreselected =
          typeof steps[index].initiallyPreselected === "function"
            ? steps[index].initiallyPreselected(options)
            : null
        steps[index] = {
          ...steps[index],
          options,
          allChoices,
          manualInputs,
          loading: false,
          impasse: false,
          completed: false,
        }

        if (
          initiallyPreselected &&
          Object.keys(initiallyPreselected).length !== 0
        ) {
          steps[index].selected = initiallyPreselected
        }

        // Remove impasse if there was one on any of the steps
        // Prevents showing impasse message when getting back to the step
        steps = steps.map((step) => ({
          ...step,
          impasse: false,
        }))
        // If there is only one option, automatically select the only value available
        if (options.length === 1) {
          steps[index].selected = options[0].value
          steps[index].completed = true
          steps[index].completionSource = "autoSelectOneOption"
        }
      })
      return {
        loading: false,
        steps,
        currentStep: this.currentStepBasedOnCompleted(this.state.steps),
      }
    })
  }

  /**
   * @typedef {import('./carAssemblageTypes').StepConfig} StepConfig
   * @param {StepConfig} stepConfig
   * @todo add proper type for data
   */
  mapApiResponseToStepChoices = (stepConfig, data) => {
    let choices
    const valueToLabelMapper = this.getValueToLabelMapper(stepConfig)
    if (stepConfig.apiIds) {
      // When the step's options can be mapped from multiple keys in `possible_choices`
      choices = []
      stepConfig.apiIds.forEach((apiId) => {
        choices.push({
          category:
            this.props.labels.categoryLabels[stepConfig.stepName][apiId],
          options: data["possible_choices"][apiId]
            .map((attrName) => ({ [attrName]: true }))
            .map(valueToLabelMapper)
            .sort((a, b) =>
              a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1
            ), // Sorted by label
        })
      })
    } else {
      choices =
        data["possible_choices"][stepConfig.stepName].map(valueToLabelMapper)
    }
    return choices
  }

  getValueToLabelMapper = (stepConfig) => (value) => ({
    label: stepConfig?.valueToLabelMapper?.(value),
    value,
  })

  findStepIndexByApiIdOrName = (steps, stepName) => {
    return steps.findIndex((step) =>
      step.apiIds ? step.apiIds.includes(stepName) : step.stepName === stepName
    )
  }

  impasseAtStep = (stepIndex) => {
    this.setState((prevState) => {
      let steps = [...prevState.steps]
      steps[stepIndex] = {
        ...steps[stepIndex],
        impasse: true,
        completed: false,
      }
      // Also go back to stepIndex, so that the user can correct the error
      return { steps, currentStep: stepIndex }
    })
    this.props.trackFunction({
      entity: "car_form",
      action: "displayed",
      element: `select option: ${this.state.steps[stepIndex].stepName}`,
      flow: "trade_in",
      type: "system_exception",
    })
  }

  navigatedToStep = (stepIndex) => {
    this.props.trackFunction({
      entity: "car_form",
      action: "displayed",
      element: `select option: ${this.state.steps[stepIndex].stepName}`,
      details: "reopened",
      flow: "trade_in",
      type: "micro_proceeding",
    })
    this.setState({ currentStep: stepIndex })
  }

  scrollToStep(stepIndex) {
    if (!this.props.scrollStepsIntoView) return
    const scrollIntoViewOptions = {
      block: "start",
      behavior: "smooth",
    }
    const stepElement = this.stepsRef[stepIndex].current
    if (this.props.isSellYourCar) {
      stepElement.scrollIntoView(scrollIntoViewOptions)
    } else {
      stepElement.scrollIntoViewIfNeeded(scrollIntoViewOptions)
    }
  }

  clearStepSelected = (stepIndex) => {
    this.setState((prevState) => {
      let steps = [...prevState.steps]
      steps[stepIndex] = {
        ...steps[stepIndex],
        selected: null,
        completed: false,
      }
      return { steps }
    })
  }

  // Renders children to append to a step, outside the step component itself
  renderAppendToStep = (stepIndex, appendedChildren) => {
    this.setState((prevState) => {
      let steps = [...prevState.steps]
      steps[stepIndex] = { ...steps[stepIndex], appendedChildren }
      return { steps }
    })
  }

  /**
   * Renders the proceed button.
   * Either used directly in render, or used in the prop
   * onRenderProceedButton for the parent
   *
   * @returns {boolean|*|JSX.Element}
   */
  renderProceedButton = () => {
    const disableCta = {
      color: this.props.theme.color,
      border: `1px solid ${this.props.theme.color}`,
    }

    const ButtonComponent = this.props.buttonComponent

    return (
      (this.areStepsComplete() || !this.props.hideFutureSteps) &&
      !["equipment", "email_address"].includes(
        (this.state.steps[this.state.currentStep] || {}).stepName
      ) &&
      (this.props.onProceedButtonRender ? (
        <ButtonComponent
          disabled={!this.areStepsComplete() && !this.props.ctaAlwaysEnabled}
          onClick={this.state.finalized ? null : this.finished}
        >
          {this.props.labels.carAssemblageDone}
        </ButtonComponent>
      ) : (
        <div className={styles.confirmationStep}>
          <button
            style={this.areStepsComplete() ? {} : disableCta}
            type="button"
            onClick={this.state.finalized ? null : this.finished}
            className={`${this.areStepsComplete() || this.props.ctaAlwaysEnabled
                ? "main-cta"
                : styles.mainCtaDisabled
              }`}
            disabled={!this.areStepsComplete() && !this.props.ctaAlwaysEnabled}
          >
            {this.state.finalized && (
              <span data-testid="spinner">
                <MDSpinner
                  singleColor="#fff"
                  size={18}
                  className={styles.buttonSpinner}
                />
              </span>
            )}
            {this.props.labels.carAssemblageDone}
          </button>
        </div>
      ))
    )
  }

  render() {
    const { theme } = this.props
    const { steps, currentStep, loading } = this.state
    const { labels, hideFutureSteps } = this.props
    const singleChoiceSteps = steps.slice(this.nonSingleStepsNum)

    return (
      <form>
        {this.props.isSellYourCar ? (
          <>
            <NonSingleStep
              theme={theme}
              ref={this.stepsRef[0]}
              active={currentStep === 0}
              step={steps[0]}
              index={0}
              labels={labels}
              navigatedTo={this.navigatedToStep}
              hideStepsArrow={this.props.hideStepsArrow}
              headline={this.props.labels.headlines["make"]}
            >
              <BrandSelector
                theme={theme}
                {...steps[0].options}
                labels={labels}
                initiallySelected={steps[0].selected}
                doneCallback={(data) => {
                  const stepsCloned = [...this.state.steps]
                  stepsCloned[1].selected = null
                  stepsCloned[1].completed = false

                  this.setState(
                    {
                      steps: stepsCloned,
                    },
                    () => {
                      this.changeStepSelected(0, data, {
                        forceComplete: true,
                        completionSource: "user",
                      })
                    }
                  )
                }}
              />
            </NonSingleStep>
            <NonSingleStep
              theme={theme}
              ref={this.stepsRef[1]}
              active={currentStep === 1}
              step={steps[1]}
              index={1}
              labels={labels}
              navigatedTo={this.navigatedToStep}
              headline={this.props.labels.headlines["model"]}
              hideStepsArrow={this.props.hideStepsArrow}
            >
              <ModelSelector
                theme={theme}
                {...steps[1].options}
                labels={labels}
                autoFocus={this.props.autoFocusBrandModel}
                selectedBrand={steps[0].selected?.brand}
                initiallySelected={steps[1].selected}
                initialText={this.props.initialBrandModelText}
                doneCallback={(data) =>
                  this.changeStepSelected(1, data, {
                    forceComplete: true,
                    completionSource: "user",
                  })
                }
              />
            </NonSingleStep>
          </>
        ) : (
          <NonSingleStep
            theme={theme}
            ref={this.stepsRef[0]}
            active={currentStep === 0}
            step={steps[0]}
            index={0}
            labels={labels}
            navigatedTo={this.navigatedToStep}
            headline={this.props.labels.headlines["brand_and_model_name"]}
            hideStepsArrow={this.props.hideStepsArrow}
          >
            <BrandAndModelSelector
              theme={theme}
              {...steps[0].options}
              labels={labels}
              autoFocus={this.props.autoFocusBrandModel}
              initiallySelected={steps[0].selected}
              initialText={this.props.initialBrandModelText}
              doneCallback={(data) =>
                this.changeStepSelected(0, data, {
                  forceComplete: true,
                  completionSource: "user",
                })
              }
            />
          </NonSingleStep>
        )}
        {singleChoiceSteps.map((step, i) => {
          const stepIndex = i + this.nonSingleStepsNum
          const StepComponent = step.component || SingleChoice
          const lastStep = this.state.steps.length - 1 === stepIndex
          const alwaysActive =
            this.props.isSellYourCar && lastStep && this.areStepsComplete()
          if (
            hideFutureSteps &&
            !alwaysActive &&
            !this.areStepsComplete() &&
            currentStep < stepIndex
          )
            return
          return (
            <React.Fragment key={`step${stepIndex}`}>
              <Step
                theme={theme}
                ref={this.stepsRef[stepIndex]}
                key={`step${stepIndex}`}
                loading={loading}
                active={currentStep === stepIndex || alwaysActive}
                nextStep={stepIndex === currentStep + 1}
                past={this.areStepsComplete() || currentStep > stepIndex}
                {...step}
                index={stepIndex}
                navigatedTo={this.navigatedToStep}
                labels={labels}
                headline={labels.headlines[step.stepName]}
                appendedChildren={step.appendedChildren}
                hideStepsArrow={this.props.hideStepsArrow}
              >
                <StepComponent
                  {...step}
                  color={theme.color}
                  theme={theme}
                  choices={step.options}
                  initiallySelected={step.selected}
                  lastStep={lastStep}
                  stepName={step.stepName}
                  kmPlaceholder={this.props.kmPlaceholder}
                  {...step.componentProps}
                  locale={this.props.locale}
                  trackFunction={this.props.trackFunction}
                  toggle={step.stepName === "year"}
                  clearStepSelected={() => this.clearStepSelected(stepIndex)}
                  doneCallback={(data) =>
                    this.changeStepSelected(stepIndex, data, {
                      forceComplete: true,
                      completionSource: "user",
                      callback:
                        lastStep &&
                          (step.stepName === "email_address" ||
                            step.stepName === "equipment")
                          ? this.finished
                          : null,
                    })
                  }
                  onCapturePostCode={this.props.onCapturePostCode}
                  onCaptureBuyerEmailAddress={
                    this.props.onCaptureBuyerEmailAddress
                  }
                  renderAppendToStep={(appendedChildren) => {
                    if (
                      step.stepName === "equipment" &&
                      !this.props.isSellYourCar &&
                      this.props.onProceedButtonRender
                    ) {
                      this.props.onProceedButtonRender(appendedChildren)
                    } else {
                      this.renderAppendToStep(stepIndex, appendedChildren)
                    }
                  }}
                  labels={labels}
                  emailAddressTranslations={this.props.emailAddressTranslations}
                  buttonComponent={
                    step.stepName === "equipment" && this.props.isSellYourCar
                      ? undefined
                      : this.props.buttonComponent
                  }
                  isSellYourCar={this.props.isSellYourCar}
                />
              </Step>
            </React.Fragment>
          )
        })}

        {/* Do not show the final button when it reaches the equipment */}
        {!this.props.hideFormButton &&
          !this.props.onProceedButtonRender &&
          this.renderProceedButton()}
      </form>
    )
  }
}

export default CarAssemblage
